import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from '@design-system';
import { AlertCircle, ArrowLeft, Briefcase, ChevronRight, ClipboardList, Mail } from 'lucide-react';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import {
  AdvisorPhoneVerificationFlow,
  AdvisorSignupCardHeader,
} from '@client/components/AdvisorPhoneVerificationFlow';
import { clerkMessage, useClerkSignupFlow } from '@client/hooks/useClerkSignupFlow';
import { shouldShowSelfPlanSignupCard } from './clerk-signup-visibility';

const INTAKE_FORM_URL = 'https://app.waterlily.com/onboard/yta4-yh4?hasAccessToResults=true';

export function ClerkSignup() {
  const [searchParams] = useSearchParams();
  const showSelfPlanCard = shouldShowSelfPlanSignupCard(searchParams);
  const {
    form,
    step,
    onStartSubmit,
    onVerifyPhoneSubmit,
    handlePhoneNumberChange,
    handleCountryCodeChange,
    isStartSubmitting,
    isVerifying,
    phoneCode,
    setPhoneCode,
    emailAddress,
    phoneNumber,
    mutationError,
    clerkErrors,
    resendLink,
    resendPhone,
    goBack,
    emailLinkVerified,
    isSigningIn,
    isEmailOnlyMode,
    isResendingPhone,
    phoneCodeResent,
    isResendingLink,
    emailLinkResent,
    pilotBlockedReason,
    dismissPilotBlock,
  } = useClerkSignupFlow();

  const isFormInvalid = !form.formState.isValid;

  if (isSigningIn) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="text-primary size-8" />
          <p className="text-sm text-gray-700">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {isVerifying && (
        <div className="z-100 fixed inset-0 flex items-center justify-center bg-white/70">
          <Spinner className="text-primary size-8" />
        </div>
      )}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="pt-18 flex flex-1 flex-col items-center justify-center px-4 pb-12 md:pt-20">
        <div className="w-full max-w-md space-y-5">
          <Card className="bg-background gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
            {pilotBlockedReason ? (
              <>
                <AdvisorSignupCardHeader
                  icon={<Briefcase className="text-primary-dark size-5" />}
                  eyebrow="Advisor Sign-Up"
                  title="Create your advisor account"
                  description="We'll text you a code and email you a login link to finish setup"
                />
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <Alert variant="destructive">
                      <AlertCircle />
                      <AlertDescription>
                        Edward Jones advisor accounts are currently available only through
                        invitation to the Edward Jones pilot. You may request an invitation to the
                        pilot by sending an email to hello@joinwaterlily.com.
                      </AlertDescription>
                    </Alert>
                    <p className="text-foreground text-sm">
                      Just want a preview of Waterlily? Edward Jones team members are welcome to
                      create a personal Waterlily account to plan for their own future care needs
                      using the option below that says &ldquo;Want to plan for your own care?&rdquo;
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={dismissPilotBlock}
                    >
                      <ArrowLeft />
                      Use a different email
                    </Button>
                  </div>
                </CardContent>
              </>
            ) : step === 'start' && !isEmailOnlyMode ? (
              <Form {...form}>
                <AdvisorPhoneVerificationFlow
                  step="phone"
                  title="Create your advisor account"
                  description="We'll text you a code and email you a login link to finish setup"
                  onSubmit={onStartSubmit}
                  submitLabel="Send phone code"
                  isSubmitting={isStartSubmitting}
                  submitDisabled={isFormInvalid}
                  countryCode={form.watch('countryCode')}
                  phoneNumber={form.watch('phoneNumber')}
                  onCountryCodeChange={handleCountryCodeChange}
                  onPhoneNumberChange={value =>
                    handlePhoneNumberChange({
                      target: { value },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  beforeFields={
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <Mail className="h-4 w-4 text-gray-400" />
                            Email address
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="you@example.com"
                              className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                              autoFocus
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                          {clerkErrors.fields.emailAddress && (
                            <p className="text-destructive text-sm">
                              {clerkMessage(clerkErrors.fields.emailAddress)}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  }
                  phoneError={
                    form.formState.errors.phoneNumber?.message || clerkErrors.fields.phoneNumber ? (
                      <p className="text-destructive text-sm">
                        {form.formState.errors.phoneNumber?.message ??
                          clerkMessage(clerkErrors.fields.phoneNumber)}
                      </p>
                    ) : undefined
                  }
                  error={
                    clerkErrors.global && clerkErrors.global.length > 0 ? (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{clerkMessage(clerkErrors.global[0])}</AlertDescription>
                      </Alert>
                    ) : mutationError ? (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{mutationError}</AlertDescription>
                      </Alert>
                    ) : undefined
                  }
                  afterSubmit={
                    <>
                      <div id="clerk-captcha" />
                      <p className="text-center text-xs text-gray-500">
                        By continuing, you agree to our Terms of Service and Privacy Policy.
                      </p>
                    </>
                  }
                />
              </Form>
            ) : step === 'start' ? (
              <>
                <AdvisorSignupCardHeader
                  icon={<Briefcase className="text-primary-dark size-5" />}
                  eyebrow="Advisor Sign-Up"
                  title="Create your advisor account"
                  description="We'll email you a login link to finish setup"
                />
                <CardContent className="p-6">
                  <Form {...form}>
                    <form
                      onSubmit={onStartSubmit}
                      className="space-y-4"
                    >
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                              <Mail className="h-4 w-4 text-gray-400" />
                              Email address
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="you@example.com"
                                className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                                autoFocus
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                            {clerkErrors.fields.emailAddress && (
                              <p className="text-destructive text-sm">
                                {clerkMessage(clerkErrors.fields.emailAddress)}
                              </p>
                            )}
                          </FormItem>
                        )}
                      />
                      {clerkErrors.global && clerkErrors.global.length > 0 ? (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{clerkMessage(clerkErrors.global[0])}</AlertDescription>
                        </Alert>
                      ) : mutationError ? (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{mutationError}</AlertDescription>
                        </Alert>
                      ) : null}
                      <Button
                        type="submit"
                        className="w-full py-6"
                        disabled={isStartSubmitting || isFormInvalid}
                      >
                        {isStartSubmitting && <Spinner className="mr-2 size-4" />}
                        Send login link
                      </Button>
                      <div id="clerk-captcha" />
                      <p className="text-center text-xs text-gray-500">
                        By continuing, you agree to our Terms of Service and Privacy Policy.
                      </p>
                    </form>
                  </Form>
                </CardContent>
              </>
            ) : step === 'verify_phone' ? (
              <AdvisorPhoneVerificationFlow
                step="verify"
                title="Enter the text code"
                description="We just sent a 6-digit code to your phone"
                onSubmit={onVerifyPhoneSubmit}
                submitLabel="Verify phone"
                isSubmitting={isVerifying}
                phoneNumber={phoneNumber ?? ''}
                phoneCode={phoneCode}
                onPhoneCodeChange={setPhoneCode}
                onResend={() => resendPhone()}
                resendDisabled={isResendingPhone || phoneCodeResent}
                resendLabel={
                  isResendingPhone
                    ? 'Resending…'
                    : phoneCodeResent
                      ? 'Code resent ✓'
                      : 'Resend text code'
                }
                error={
                  mutationError || (clerkErrors.global && clerkErrors.global.length > 0) ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {clerkErrors.global && clerkErrors.global.length > 0
                          ? clerkMessage(clerkErrors.global[0])
                          : mutationError}
                      </AlertDescription>
                    </Alert>
                  ) : undefined
                }
                backAction={{ label: 'Use different email or phone', onClick: goBack }}
              />
            ) : (
              <>
                <AdvisorSignupCardHeader
                  icon={<Mail className="text-primary-dark size-5" />}
                  eyebrow="Check Your Email"
                  title="Login link sent to your email"
                  description="Click the link we just emailed to finish signing in. You can close this tab."
                />
                <CardContent className="space-y-6 p-6">
                  <div className="bg-primary/5 flex flex-col items-center gap-3 rounded-xl p-6 text-center">
                    <div className="bg-primary/10 flex size-12 items-center justify-center rounded-full">
                      <Mail className="text-primary size-6" />
                    </div>
                    <p className="text-sm text-gray-700">We sent a login link to</p>
                    <p className="text-base font-medium text-gray-950">{emailAddress}</p>
                    <p className="text-xs text-gray-600">
                      Open your inbox and click the link to finish signing in. The link expires in
                      10 minutes.
                    </p>
                  </div>

                  {emailLinkVerified ? (
                    <Alert className="border-primary/30 bg-primary/5">
                      <AlertDescription className="text-primary-dark">
                        You're signed in on the tab that opened the link. You can close this page.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                      <Spinner className="text-primary size-4" />
                      Waiting for you to click the link…
                    </div>
                  )}

                  {clerkErrors.global && clerkErrors.global.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{clerkMessage(clerkErrors.global[0])}</AlertDescription>
                    </Alert>
                  )}

                  {!emailLinkVerified && (
                    <>
                      <button
                        type="button"
                        className="text-primary hover:text-primary-hover disabled:hover:text-primary block w-full text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => resendLink()}
                        disabled={isResendingLink || emailLinkResent}
                      >
                        {isResendingLink
                          ? 'Resending…'
                          : emailLinkResent
                            ? 'Login link resent ✓'
                            : 'Resend login link'}
                      </button>

                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                        onClick={goBack}
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Use different email or phone
                      </button>
                    </>
                  )}
                </CardContent>
              </>
            )}
            <CardFooter className="items-center justify-center border-t p-6">
              {!emailLinkVerified && (
                <Link
                  to="/"
                  className="text-sm text-gray-600 transition-colors duration-200 hover:text-gray-800"
                >
                  Already have an account?{' '}
                  <span className="text-primary hidden font-medium sm:inline">Sign in</span>
                  <span className="text-primary font-medium sm:hidden">Log in</span>
                </Link>
              )}
            </CardFooter>
          </Card>

          {showSelfPlanCard && (
            <a
              href={INTAKE_FORM_URL}
              className="group block"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Card className="border-primary/20 bg-primary/5 hover:border-primary/50 rounded-2xl border p-0 shadow-none transition-all">
                <CardContent className="flex items-center gap-4 p-4 md:p-6">
                  <div className="bg-primary/20 group-hover:bg-primary flex size-12 shrink-0 items-center justify-center rounded-full transition-all">
                    <ClipboardList className="text-primary group-hover:text-primary-foreground size-6 transition-all" />
                  </div>
                  <div className="flex-1">
                    <p className="text-primary-dark group-hover:text-primary font-medium transition-all">
                      Want to plan for your own care?
                    </p>
                    <p className="text-primary-dark/80 group-hover:text-primary text-sm transition-all">
                      Fill out an intake form to get your personalized results
                    </p>
                  </div>
                  <ChevronRight className="text-muted-foreground group-hover:text-primary size-5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                </CardContent>
              </Card>
            </a>
          )}
        </div>
      </main>
    </div>
  );
}
